import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Users, Zap, Award } from "lucide-react";
import { stats } from "../mock";

const icons = [Zap, Users, TrendingUp, Award];

function Counter({ value, suffix }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const duration = 1800;
        const start = performance.now();
        const tick = (t) => {
          const p = Math.min((t - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setN(Math.round(value * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);
  return <span ref={ref} className="tabular-nums">{n}{suffix}</span>;
}

// Animated sparkline
function Sparkline({ seed = 0 }) {
  // Deterministic pseudo-random points
  const points = Array.from({ length: 10 }).map((_, i) => {
    const v = 0.4 + 0.45 * Math.abs(Math.sin((i + seed) * 1.7));
    return { x: (i / 9) * 100, y: 30 - v * 22 };
  });
  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-8 mt-3">
      <motion.path
        d={d}
        fill="none"
        stroke="rgba(255,255,255,0.85)"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, delay: 0.3, ease: "easeOut" }}
      />
      <motion.circle
        cx={points[points.length - 1].x}
        cy={points[points.length - 1].y}
        r="1.6"
        fill="white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 1.9 }}
      />
    </svg>
  );
}

export default function Stats() {
  return (
    <section className="relative py-8 lg:py-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-3xl border border-[#E5E5E5] bg-gradient-to-br from-[#5E0000] via-[#73100D] to-[#5E0000] overflow-hidden"
        >
          <div className="absolute inset-0 opacity-[0.08]" style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }} />
          {/* Subtle moving highlight */}
          <motion.div
            aria-hidden
            className="absolute -inset-x-32 -top-32 h-64 bg-gradient-to-r from-transparent via-white/10 to-transparent rotate-[8deg] blur-2xl pointer-events-none"
            animate={{ x: ["-20%", "120%"] }}
            transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
          />
          <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6 py-12 px-8 lg:py-16 lg:px-12">
            {stats.map((s, i) => {
              const Icon = icons[i % icons.length];
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.7 }}
                  className="text-center group cursor-default"
                >
                  <div className="flex items-center justify-center mb-3">
                    <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 grid place-items-center transition-transform duration-500 group-hover:scale-110 group-hover:bg-white/20">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-white transition-transform duration-500 group-hover:scale-105">
                    <Counter value={s.value} suffix={s.suffix} />
                  </div>
                  <div className="mt-2 text-[11px] md:text-xs text-white/75 uppercase tracking-[0.2em] font-medium">{s.label}</div>
                  <Sparkline seed={i + 1} />
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
